/* eslint-disable */
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cname } from '../utils'

/**
 * Button 组件封装
 *
 * - 使用 class-variance-authority 管理 variant/size 风格
 * - 支持 `asChild` 以 Slot 方式渲染为自定义标签
 * - 提供数值步进按钮 `ButtonNumber`
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary no-underline hover:underline',
        black: 'bg-[#111] text-primary-foreground shadow hover:bg-[#48494d]',
        grey: 'bg-[#F5F7FB] text-primary-foreground shadow  text-[#7587A5] hover:bg-[#e0e0e0]',
        highlight: 'bg-[#6468F7] text-primary-foreground shadow  text-[#fff] hover:bg-[#9659F2]'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cname(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'

/**
 * 数值步进按钮组合
 *
 * - 受控/非受控均可：优先使用 `value`，否则回退到 `defaultValue`
 * - 参数：
 *   - max/min/step：范围与步长
 *   - size：复用 Button 的尺寸
 *   - onChange(value)：回调当前值
 */
const ButtonNumber = React.forwardRef<
  HTMLButtonElement,
  {
    className?: string
    defaultValue?: number
    value?: number
    max?: number
    min?: number
    step?: number
    size?: 'default' | 'sm' | 'lg' | 'icon'
    onChange?: (value: number) => void
  }
>(({ className, value: userValue, defaultValue, max = 100, min = 0, step = 1, size = 'sm', onChange }, ref) => {
  if (max <= min) {
    throw new Error('max must be greater than min')
  }
  const [value, setValue] = React.useState(defaultValue)
  React.useEffect(() => {
    setValue(userValue)
  }, [userValue])

  const getDecimalCount = (value: number) => {
    return (String(value).split('.')[1] || '').length
  }
  const roundValue = (value: number, decimalCount: number) => {
    const rounder = Math.pow(10, decimalCount)
    return Math.round(value * rounder) / rounder
  }
  const valueAdd = () => {
    const sum = roundValue(value + step, getDecimalCount(step))
    const updateValue = sum > max ? max : sum
    setValue(updateValue)
    onChange?.(updateValue)
  }
  const valueReduce = () => {
    const sum = roundValue(value - step, getDecimalCount(step))
    const updateValue = sum < min ? min : sum
    setValue(updateValue)
    onChange?.(updateValue)
  }
  return (
    <div className={cname('input-border flex items-center rounded-md border bg-gray-50', className)}>
      <Button variant="ghost" size={size} disabled={value === min} onClick={valueReduce}>
        -
      </Button>
      <span className="block min-w-10 text-center">{value}</span>
      <Button variant="ghost" size={size} disabled={value === max} onClick={valueAdd}>
        +
      </Button>
    </div>
  )
})

ButtonNumber.displayName = 'ButtonNumber'

export { Button, ButtonNumber, buttonVariants }
